import React from 'react';
import PropTypes from 'prop-types';
import addMilliseconds from 'date-fns/addMilliseconds';
import { AutoSizer, Grid } from 'react-virtualized';
import format from 'date-fns/format';

import Button from '../../../../global/Button';
import PlayButtonIcon from '../../../../icons/PlayButtonIcon';
import PauseButtonIcon from '../../../../icons/PauseButtonIcon';
import AlbumSlideshowThumbnail from './AlbumSlideshowThumbnail';
import { calculateSlideshowDuration } from '../helpers';

const propTypes = {
  slideshowSlides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ).isRequired,
  slidesTotal: PropTypes.number,
  slidesOffset: PropTypes.number,
  onPlayToggle: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  isPlaying: PropTypes.bool,
  currentSlideId: PropTypes.string,
  currentSlideDuration: PropTypes.number,
  currentSlideRemainingTime: PropTypes.number,
  elapsedTime: PropTypes.number,
  totalDuration: PropTypes.number,
  defaultDuration: PropTypes.number,
  onThumbnailClick: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onLoadMore: PropTypes.func,
  loadingMore: PropTypes.bool,
  isAutoLayout: PropTypes.bool,
};
const AlbumSlideshowThumbnailMenu = ({
  slideshowSlides,
  slidesTotal,
  slidesOffset,
  onPlayToggle,
  saving,
  isPlaying,
  currentSlideId,
  currentSlideDuration,
  currentSlideRemainingTime,
  elapsedTime = 0,
  totalDuration = 0,
  defaultDuration = 0,
  onThumbnailClick,
  onDeletePhoto,
  onLoadMore,
  loadingMore,
  isAutoLayout,
}) => {
  const elapsedTimeDate = addMilliseconds(
    new Date('2021-01-01T00:00:00.000Z'),
    elapsedTime,
  );
  const totalDurationDate = addMilliseconds(
    new Date('2021-01-01T00:00:00.000Z'),
    totalDuration,
  );

  const currentSlideIndex = slideshowSlides.findIndex(
    ({ asset_image, photo, account_card }) =>
      (asset_image && asset_image.id === currentSlideId) ||
      (photo && photo.id === currentSlideId) ||
      (account_card && account_card.id === currentSlideId),
  );

  const onSectionRendered = ({ columnOverscanStopIndex }) => {
    if (!onLoadMore || loadingMore) {
      return;
    }

    if (slideshowSlides.length === slidesTotal) {
      return;
    }

    if (
      slidesOffset >= slideshowSlides.length ||
      columnOverscanStopIndex + 1 + slidesOffset >= slideshowSlides.length
    ) {
      onLoadMore();
    }
  };

  const cellRenderer = ({ columnIndex, key, style }) => {
    const slideshowSlide = slideshowSlides[columnIndex];

    return (
      <AlbumSlideshowThumbnail
        key={key}
        slideshowSlide={slideshowSlide}
        style={style}
        isActive={
          currentSlideId === slideshowSlide.asset_image?.id ||
          currentSlideId === slideshowSlide.photo?.id ||
          currentSlideId === slideshowSlide.account_card?.id
        }
        isSeen={
          elapsedTime >=
          calculateSlideshowDuration(
            slideshowSlides,
            defaultDuration,
            columnIndex + 1,
          )
        }
        slideDuration={
          currentSlideId === slideshowSlide.asset_image?.id ||
          currentSlideId === slideshowSlide.photo?.id ||
          currentSlideId === slideshowSlide.account_card?.id
            ? currentSlideDuration
            : undefined
        }
        slideRemainingTime={
          currentSlideId === slideshowSlide.asset_image?.id ||
          currentSlideId === slideshowSlide.photo?.id ||
          currentSlideId === slideshowSlide.account_card?.id
            ? currentSlideRemainingTime
            : undefined
        }
        onClick={() =>
          onThumbnailClick({
            slideId:
              slideshowSlide.asset_image?.id ??
              slideshowSlide.photo?.id ??
              slideshowSlide.account_card?.id,
            slideIdx: columnIndex,
          })
        }
        disabled={saving}
        onDelete={
          !onDeletePhoto || isPlaying || !!slideshowSlide.account_card
            ? undefined
            : () =>
                onDeletePhoto({
                  photoId:
                    slideshowSlide.asset_image?.id || slideshowSlide.photo?.id,
                  isAssetImage: !!slideshowSlide.asset_image,
                })
        }
      />
    );
  };

  return (
    <div className="slideshow-grid-viewer-thumbnail-menu-wrapper">
      <div className="d-flex flex-column align-items-center mx-2">
        <Button btnStyle="link" onClick={onPlayToggle} className="mb-1">
          {isPlaying ? (
            <PauseButtonIcon size={48} />
          ) : (
            <PlayButtonIcon size={48} />
          )}
        </Button>
        <p className="text-center text-white text-xs mb-0">
          {' '}
          {format(elapsedTimeDate, 'mm:ss')}/
          {format(totalDurationDate, 'mm:ss')}
        </p>
      </div>
      <div className="slideshow-grid-viewer-vertical-separator" />
      {/* Width will equal AutoSizer width - the Play Button (70px) + Margin (2rem)) */}
      <AutoSizer disableHeight height={88}>
        {({ width }) => (
          <Grid
            rowCount={1}
            width={width - (70 + 32)}
            height={isAutoLayout ? 71 : 88}
            rowHeight={isAutoLayout ? 55 : 72}
            columnWidth={110}
            columnCount={slideshowSlides.length}
            cellRenderer={cellRenderer}
            onSectionRendered={onSectionRendered}
            containerStyle={{ overflow: 'visible' }}
            scrollToColumn={currentSlideIndex}
            className="slideshow-grid-viewer-thumbnail-menu"
          />
        )}
      </AutoSizer>
    </div>
  );
};

AlbumSlideshowThumbnailMenu.propTypes = propTypes;

export default AlbumSlideshowThumbnailMenu;
